import { useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  type Resolver,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Error, Input } from '@/ui/input/input';
import { Inline, Stack } from '@/ui/line/line';

import { TextField } from '@/ui/input/text-field';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getContentItemValidationSchema,
  type ContentItemFormFields,
  type ContentItemFormInput,
} from './content-item-validation-schema';
import { useAtom } from 'jotai';
import { contentItemInEditModeState } from '../../feed.state';
import { ContentItemCategory } from '@/api/v4/content-items.api';
import { useCreateContentItem } from '../use-create-content-item.mutation';
import { useUpdateContentItem } from '../use-update-content-item.mutation';
import { isEmpty } from 'lodash-es';
import { TagsSelect } from '@/components/tag/tags-select';
import { FormLabel } from '@/components/form/form-label';
import { FormSingleDatePicker } from '@/components/form/form-single-date-picker';

import type { IOption } from '@/types';
import { FormSelectWithPredefinedOptions } from '@/components/form/form-select-with-predefined-options';
import { ContentItemFormImage } from './content-item-form-image';
import { ContentItemProjectSelect } from './content-item-project-select';
import { formatDate } from '@/helpers/format-date';
import { CALENDAR_DATE_FORMAT } from '@/constants';
import { useToggleContentItemSidebar } from '../use-toggle-content-item-sidebar';
import { prepareLink } from '@/utils/link';

interface Props {
  onCancel: () => void;
}

export const ContentItemForm = ({ onCancel }: Props) => {
  const { t } = useTranslation('settings', { keyPrefix: 'feedTab' });
  const [contentItemInEditMode] = useAtom(contentItemInEditModeState);

  const { createContentItem } = useCreateContentItem();
  const { updateContentItem } = useUpdateContentItem();

  const { navigateToFeedTab } = useToggleContentItemSidebar();

  const methods = useForm<ContentItemFormInput, unknown, ContentItemFormFields>(
    {
      mode: 'all',
      reValidateMode: 'onBlur',
      resolver: zodResolver(getContentItemValidationSchema()) as Resolver<
        ContentItemFormInput,
        unknown,
        ContentItemFormFields
      >,
      defaultValues: {
        title: contentItemInEditMode?.title ?? '',
        date:
          contentItemInEditMode?.uploadedDate ??
          formatDate(new Date(), {
            format: CALENDAR_DATE_FORMAT,
          }),
        description: contentItemInEditMode?.description ?? '',
        longDescription: contentItemInEditMode?.longDescription ?? '',
        contentUrl: contentItemInEditMode?.contentUrl ?? '',
        tags:
          contentItemInEditMode?.tags?.map(t => ({
            value: t.id,
            label: t.name,
          })) ?? [],
        category: contentItemInEditMode?.category
          ? {
              value: contentItemInEditMode.category,
              label: t([`categories.${contentItemInEditMode.category}`]) ?? '',
            }
          : null,
        file: null,
        project: contentItemInEditMode?.project
          ? {
              label: contentItemInEditMode.project.name,
              value: contentItemInEditMode.project.id,
            }
          : null,
      },
    },
  );

  const {
    formState,
    getFieldState,
    handleSubmit,
    register,
    reset,
    setValue,
    trigger,
    watch,
  } = methods;

  const ref = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset, []);

  const options = useMemo(() => {
    return [
      {
        value: ContentItemCategory.businessOpportunity,
        label:
          t([`categories.${ContentItemCategory.businessOpportunity}`]) ?? '',
      },
      {
        value: ContentItemCategory.materials,
        label: t([`categories.${ContentItemCategory.materials}`]) ?? '',
      },
      {
        value: ContentItemCategory.trends,
        label: t([`categories.${ContentItemCategory.trends}`]) ?? '',
      },
    ];
  }, [t]);

  const handleCategoryChange = async (value: IOption) => {
    const category = value as ContentItemFormInput['category'];
    setValue('category', category, { shouldDirty: true, shouldValidate: true });

    if (category?.value === ContentItemCategory.materials) {
      await trigger();
      return;
    }

    setValue('project', null, { shouldDirty: true, shouldValidate: true });
    await trigger();
  };

  const submitNewArticle = async (values: ContentItemFormFields) => {
    if (!values.category) return;

    await createContentItem({
      title: values.title,
      description: values.description,
      ...(values.longDescription && {
        longDescription: values.longDescription,
      }),
      uploadedDate: values.date,
      tagIds: values.tags.map(t => t.value),
      category: values.category.value,
      contentUrl: prepareLink(values.contentUrl),
      ...(values.category.value === ContentItemCategory.materials &&
        values.project && {
          projectId: values.project.value,
        }),
      ...(values.file && { file: values.file }),
    });

    navigateToFeedTab();
  };

  const submitUpdatedArticle = async (values: ContentItemFormFields) => {
    if (!contentItemInEditMode) {
      return;
    }

    if (!values.category) return;

    if (isEmpty(formState.dirtyFields)) {
      onCancel();
      return;
    }

    await updateContentItem({
      contentItemId: contentItemInEditMode.id,
      title: values.title,
      description: values.description,
      ...(values.longDescription && {
        longDescription: values.longDescription,
      }),
      uploadedDate: values.date,
      tagIds: values.tags.map(t => t.value),
      category: values.category.value,
      contentUrl: prepareLink(values.contentUrl),
      ...(values.category.value === ContentItemCategory.materials &&
        values.project && {
          projectId: values.project.value,
        }),
      ...(values.file && { file: values.file }),
    });

    navigateToFeedTab();
  };

  return (
    <FormProvider {...methods}>
      <Flexbox
        name="content-item-form-container"
        gap="24px"
        defaultRef={ref}
        direction="column"
        flex={1}
        fullWidth
      >
        <Stack gap="16px">
          <Inline gap="6px" templateColumns="3fr 2fr">
            <FormLabel
              name="article-name"
              label={t`articleForm.title`}
              position="top"
            >
              <Input
                padding="4px 8px"
                fontSize="12px"
                placeholder={t`articleForm.titlePlaceholder`}
                value={watch('title')}
                error={getFieldState('title', formState).error?.message}
                isErrorAbsolute
                errorAlign="right"
                {...register('title')}
              />
            </FormLabel>
            <FormSingleDatePicker<ContentItemFormInput>
              formContext={methods}
              fieldName="date"
              label={t`articleForm.uploadingDate`}
              labelPosition="top"
            />
          </Inline>

          <FormLabel
            name="description"
            label={t`articleForm.description`}
            position="top"
          >
            <Controller
              control={methods.control}
              data-name="controller"
              name="description"
              render={({ field, fieldState }) => (
                <>
                  <DescriptionInput
                    dataCy="description"
                    placeholder={t`articleForm.descriptionPlaceholder`}
                    value={watch('description')}
                    onChange={field.onChange}
                  />
                  {fieldState.error ? (
                    <Error>{fieldState.error.message}</Error>
                  ) : null}
                </>
              )}
            />
          </FormLabel>

          {watch('category')?.value === ContentItemCategory.trends && (
            <FormLabel
              name="long-description"
              label={t`articleForm.longDescription`}
              position="top"
            >
              <Controller
                control={methods.control}
                data-name="controller"
                name="longDescription"
                render={({ field }) => (
                  <LongDescriptionInput
                    dataCy="long-description"
                    placeholder={t`articleForm.longDescriptionPlaceholder`}
                    value={watch('longDescription') ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormLabel>
          )}
        </Stack>

        <FormLabel
          name="content-url"
          label={t`articleForm.contentUrl`}
          position="top"
        >
          <Input
            padding="4px 8px"
            fontSize="12px"
            placeholder={t`articleForm.contentUrlPlaceholder`}
            value={watch('contentUrl')}
            error={getFieldState('contentUrl', formState).error?.message}
            isErrorAbsolute
            errorAlign="right"
            {...register('contentUrl')}
          />
        </FormLabel>

        <FormLabel
          data-name="category-label"
          name="category"
          label={t`articleForm.category`}
          position="top"
        >
          <Controller
            control={methods.control}
            data-name="controller"
            name="category"
            render={({ field }) => (
              <FormSelectWithPredefinedOptions
                id="category"
                options={options}
                defaultValue={field.value ? [field.value] : undefined}
                onChange={handleCategoryChange}
                placeholder={t`articleForm.categoryPlaceholder`}
              />
            )}
          />
        </FormLabel>
        {watch('category')?.value === ContentItemCategory.materials && (
          <ContentItemProjectSelect />
        )}
        <FormLabel
          data-name="category-label"
          name="tagIds"
          label={t`articleForm.tags`}
          position="top"
        >
          <Controller
            control={methods.control}
            data-name="controller"
            name="tags"
            render={({ field, fieldState }) => (
              <>
                <TagsSelect
                  id="tags"
                  defaultValue={field.value}
                  onChange={field.onChange}
                  placeholder={t`articleForm.tagsPlaceholder`}
                />
                {fieldState.error ? (
                  <Error>{fieldState.error.message}</Error>
                ) : null}
              </>
            )}
          />
        </FormLabel>

        <Controller
          control={methods.control}
          name="file"
          render={({ field, fieldState }) => (
            <ContentItemFormImage
              uploadedFile={field.value}
              imageMetadata={
                contentItemInEditMode ? contentItemInEditMode.image : null
              }
              onUploadFile={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <SubmitButtonsContainer
          name="content-item-form-submit-container"
          justify="flex-end"
        >
          <Inline gap="10px">
            <Button
              variant="underlined"
              onClick={onCancel}
            >{t`articleForm.cancel`}</Button>
            <Button
              onClick={
                contentItemInEditMode
                  ? handleSubmit(submitUpdatedArticle)
                  : handleSubmit(submitNewArticle)
              }
              isLoading={formState.isSubmitting}
              disabled={!formState.isValid || formState.isSubmitting}
            >
              {t(
                contentItemInEditMode
                  ? 'articleForm.updateItem'
                  : 'articleForm.addItem',
              )}
            </Button>
          </Inline>
        </SubmitButtonsContainer>
      </Flexbox>
    </FormProvider>
  );
};

const DescriptionInput = styled(TextField)`
  font-size: 12px;
  height: 140px;
  overflow: auto;
`;

const LongDescriptionInput = styled(TextField)`
  font-size: 12px;
  height: 200px;
  overflow: auto;
`;

const SubmitButtonsContainer = styled(Flexbox)`
  margin-top: auto;
`;
